import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import { DEFAULT } from './constants';

export const ScrollerContext = React.createContext({});

/* eslint-disable react/prop-types */

/**
 * Higher Order Component
 * Adds a scroller state to a component's props
 * @param {*} Component React Component
 * @param {*} scrollerId Unique id of the scroller state needed
 * @returns React Component with state of scrollerId provided added to props
 *
 * scrollerId can be passed in one of two ways:
 * - include scrollerId on the component's props
 * - pass the scrollerId along with the Component wrapped
 */
export const withScrollerContext = (Component, scrollerId) => (props) => {
	const id = scrollerId || props?.scrollerId || DEFAULT;
	const { [id]: scrollerContext } = useContext(ScrollerContext);
	const scroller = useContext(scrollerContext);

	return <Component {...props} scroller={scroller} />;
};

/**
 * Higher Order Component
 * Adds enable and disable functions for a particular scroller
 * to a component's props
 * @param {*} Component React Component
 * @param {*} scrollerId Unique id of the scroller state needed
 * @returns React Component with state of scrollerId provided added to props
 *
 * scrollerId can be passed in one of two ways:
 * - include scrollerId on the component's props
 * - pass the scrollerId along with the Component wrapped
 */
export const withEnableDisableScroller = (Component, scrollerId) => (props) => {
	const id = scrollerId || props?.scrollerId || DEFAULT;
	const { [id]: scrollerContext } = useContext(ScrollerContext);
	const { enableScroller, disableScroller } = useContext(scrollerContext);

	return <Component {...props} enableScroller={enableScroller} disableScroller={disableScroller} />;
};

/**
 * Higher Order Component
 * Adds enable and disable functions for a particular scroller
 * to a component's props
 * @param {*} Component React Component
 * @param {*} scrollerId Unique id of the scroller state needed
 * @returns React Component with state of scrollerId provided added to props
 *
 * scrollerId can be passed in one of two ways:
 * - include scrollerId on the component's props
 * - pass the scrollerId along with the Component wrapped
 */
export const withScrollingElement = (Component, scrollerId) => (props) => {
	const id = scrollerId || props?.scrollerId || DEFAULT;
	const { [id]: scrollerContext } = useContext(ScrollerContext);
	const { scrollingElement } = useContext(scrollerContext);

	return <Component {...props} scrollingElement={scrollingElement} />;
};

/**
 * Higher Order Component
 * Adds enable and disable functions for a particular scroller
 * to a component's props
 * @param {*} Component React Component
 * @param {*} scrollerId Unique id of the scroller state needed
 * @returns React Component with state of scrollerId provided added to props
 *
 * scrollerId can be passed in one of two ways:
 * - include scrollerId on the component's props
 * - pass the scrollerId along with the Component wrapped
 */
export const withForceUpdateResize = (Component, scrollerId) => (props) => {
	const id = scrollerId || props?.scrollerId || DEFAULT;
	const { [id]: scrollerContext } = useContext(ScrollerContext);
	const { forceUpdate, forceResize } = useContext(scrollerContext);

	return <Component {...props} forceUpdate={forceUpdate} forceResize={forceResize} />;
};

/* eslint-enable react/prop-types */

/**
 * A context of contexts
 * This holds a reference to each scroller's context
 * as Scrollers can be dynamically generated
 * and so not easily exported from a static file
 */
class ScrollerContextProvider extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			setScrollerContext: this.setScrollerContext,
			removeScrollerContext: this.removeScrollerContext,
		};
	}

	setScrollerContext = (scrollerId, contextReference) => {
		this.setState({ [scrollerId]: contextReference });
	};

	removeScrollerContext = (scrollerId) => {
		this.setState((prevState) => {
			const newState = { ...prevState };
			delete newState[scrollerId];
			return newState;
		});
	};

	render() {
		return <ScrollerContext.Provider value={this.state}>{this.props.children}</ScrollerContext.Provider>;
	}
}

ScrollerContextProvider.propTypes = {
	children: PropTypes.node,
};

export default ScrollerContextProvider;
